// src/pages/About.tsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Shield,
  Target,
  Users,
  Globe,
  Award,
  Heart,
  Zap,
  BarChart,
  Wallet,
  Lock,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  Key,
  Cpu,
  Search,
  RefreshCw,
  ShieldOff,
  Eye,
  Coins,
  TrendingUp,
  FileText,
  MessageSquare,
  Scale,
  ZapOff,
  CheckCircle2,
} from "lucide-react";
import logo from "../assets/images/logo.png";
import PublicNavbar from "../components/PublicNavbar";
import AOS from "aos";
import "aos/dist/aos.css";
import CountUp from "react-countup";
import { useInView } from "react-intersection-observer";

const About: React.FC = () => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);
  const [fade, setFade] = useState(true);

  const heroTexts = [
    {
      main: "Professional Crypto Recovery",
      highlight: "Trusted Expertise",
      color: "from-primary to-primary/80",
    },
    {
      main: "Asset Recovery Specialists",
      highlight: "When Cryptocurrency is Lost",
      color: "from-blue-500 to-blue-400",
    },
    {
      main: "Blockchain Forensic Experts",
      highlight: "For Digital Asset Recovery",
      color: "from-emerald-500 to-emerald-400",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentTextIndex((prev) => (prev + 1) % heroTexts.length);
        setFade(true);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    AOS.init({
      duration: 600,
      easing: "ease-out-cubic",
      once: false,
    });
  }, []);

  const { ref: statsRef, inView: statsInView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-950">
      {/* Navigation */}
      <PublicNavbar currentPage="/about" />

      {/* Hero Section */}
      <section className="relative overflow-hidden pt-24 pb-20 lg:pt-28 lg:pb-24">
        {/* Animated Background Elements */}
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-primary/5 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-blue-500/5 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 lg:px-5 py-20 relative">
          <div className="text-center max-w-4xl mx-auto">
            <div
              className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 text-primary text-sm font-medium mb-6 backdrop-blur-sm"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <ShieldCheck className="h-4 w-4 mr-2" />
              Trusted Crypto Recovery Since 2022
            </div>

            {/* Rotating Hero Text */}
            <div className="h-48 lg:h-40 mb-8">
              <div
                className={`transition-all duration-700 ${
                  fade
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 -translate-y-4"
                }`}
              >
                <h1 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white mb-8">
                  {heroTexts[currentTextIndex].main}
                  <span
                    className={`block bg-gradient-to-r ${heroTexts[currentTextIndex].color} bg-clip-text text-transparent`}
                  >
                    {heroTexts[currentTextIndex].highlight}
                  </span>
                </h1>
              </div>
            </div>

            <p
              className="text-lg lg:text-xl text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Pelivor Recovery is a specialized cryptocurrency recovery firm
              dedicated to helping individuals and businesses recover lost,
              stolen, or inaccessible digital assets through expert forensic
              analysis and professional recovery services.
            </p>
          </div>
        </div>
      </section>

      {/* Our Mission Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission & Vision
              </h2>
              <div className="space-y-4 text-gray-600 dark:text-gray-400">
                <p data-aos="fade-right" data-aos-delay="200">
                  Founded in 2022 by cybersecurity and blockchain experts,
                  Pelivor Recovery was created to address the growing need for
                  professional cryptocurrency recovery services in an
                  increasingly complex digital asset landscape.
                </p>
                <p data-aos="fade-right" data-aos-delay="300">
                  We believe that lost cryptocurrency should not be considered
                  permanently inaccessible. Our team combines technical
                  expertise, legal knowledge, and blockchain forensic skills to
                  recover assets that others have deemed unrecoverable.
                </p>
                <p data-aos="fade-right" data-aos-delay="400">
                  Our vision is to become the most trusted name in
                  cryptocurrency recovery, providing hope and solutions to those
                  who have lost access to their digital wealth.
                </p>
              </div>
            </div>
            <div
              className="grid grid-cols-2 gap-6"
              data-aos="fade-left"
              data-aos-delay="500"
            >
              {/* Mission Card */}
              <div
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-primary transition-all duration-300 hover:scale-[1.02] group/card"
                data-aos="zoom-in"
                data-aos-delay="100"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Target className="h-6 w-6 text-primary" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Our Mission
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  To recover lost cryptocurrency assets through expert analysis
                  and professional recovery services
                </p>
              </div>

              {/* Global Card */}
              <div
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-blue-500 transition-all duration-300 hover:scale-[1.02] group/card"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Globe className="h-6 w-6 text-blue-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Global Reach
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Serving clients across{" "}
                  {statsInView && <CountUp end={50} suffix="+" duration={2} />}{" "}
                  countries with localized expertise
                </p>
              </div>

              {/* Expertise Card */}
              <div
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-emerald-500 transition-all duration-300 hover:scale-[1.02] group/card"
                data-aos="zoom-in"
                data-aos-delay="300"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <Award className="h-6 w-6 text-emerald-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Expert Team
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Certified blockchain forensic analysts and recovery
                  specialists
                </p>
              </div>

              {/* Success Card */}
              <div
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl p-6 rounded-2xl border border-gray-200/30 dark:border-gray-700/30 hover:border-purple-500 transition-all duration-300 hover:scale-[1.02] group/card"
                data-aos="zoom-in"
                data-aos-delay="400"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-500/5 rounded-xl flex items-center justify-center mb-4 group-hover/card:scale-110 transition-transform">
                  <TrendingUp className="h-6 w-6 text-purple-500" />
                </div>
                <h3 className="font-bold text-gray-900 dark:text-white mb-2">
                  Proven Success
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {statsInView && <CountUp end={78} suffix="%" duration={2} />}{" "}
                  successful recovery rate across cases
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div
            className="text-center mb-12 lg:mb-16"
            data-aos="fade-up"
            data-aos-delay="100"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              The principles that guide our work and define our approach to
              cryptocurrency recovery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {/* Transparency Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-primary/5 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Eye className="h-10 w-10 text-primary" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Complete Transparency
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Clear communication, honest assessments, and regular updates
                throughout the recovery process
              </p>
            </div>

            {/* Client Focus Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-blue-500/5 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Heart className="h-10 w-10 text-blue-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Client-Centered Approach
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Every case is treated with personalized attention, empathy, and
                dedication to achieving the best possible outcome
              </p>
            </div>

            {/* Integrity Value */}
            <div
              className="text-center group/value"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <div className="relative mx-auto mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 rounded-full blur-lg"></div>
                <div className="relative w-20 h-20 bg-white dark:bg-gray-800 rounded-full flex items-center justify-center border border-gray-200/30 dark:border-gray-700/30 group-hover/value:scale-110 transition-transform">
                  <Scale className="h-10 w-10 text-emerald-500" />
                </div>
              </div>
              <h3 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-4">
                Professional Integrity
              </h3>
              <p className="text-gray-600 dark:text-gray-400 px-4">
                Adherence to the highest ethical standards and legal compliance
                in all recovery operations
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div data-aos="fade-right" data-aos-delay="100">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-6">
                Our Expertise & Technology
              </h2>
              <div className="space-y-6">
                {/* Forensic Analysis Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="200"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <Search className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Advanced Forensic Analysis
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Sophisticated blockchain analysis tools and techniques to
                      trace transactions and identify recovery opportunities
                      across multiple networks
                    </p>
                  </div>
                </div>

                {/* Legal Recovery Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="300"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-500/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <FileText className="h-6 w-6 text-blue-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Legal Recovery Framework
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Comprehensive legal coordination with authorities and
                      compliance with international regulations for asset
                      recovery
                    </p>
                  </div>
                </div>

                {/* Security Technology Feature */}
                <div
                  className="flex items-start group/feature"
                  data-aos="fade-right"
                  data-aos-delay="400"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl flex items-center justify-center mr-4 flex-shrink-0 group-hover/feature:scale-110 transition-transform">
                    <Lock className="h-6 w-6 text-emerald-500" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Security Technology
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400">
                      Enterprise-grade security protocols and encryption to
                      protect client data and ensure confidential handling of
                      sensitive information
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Content - Expertise Card */}
            <div className="relative" data-aos="fade-left" data-aos-delay="500">
              <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-primary/2 to-transparent rounded-3xl blur-xl opacity-50"></div>

              <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-6 lg:p-8">
                <div className="space-y-6">
                  {/* Success Rate Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 group/card hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="100"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-primary/20 group-hover/card:scale-110 transition-transform">
                        <BarChart className="h-6 w-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          Recovery Success Rate
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          <span ref={statsRef}>
                            {statsInView && (
                              <CountUp end={78} suffix="%" duration={2} />
                            )}
                          </span>{" "}
                          success rate in recovering assets across various
                          scenarios
                        </p>
                      </div>
                      <Sparkles className="h-4 w-4 text-primary opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>

                  {/* Case Volume Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-blue-500/5 border border-blue-500/20 group/card hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="200"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-blue-500/20 group-hover/card:scale-110 transition-transform">
                        <Coins className="h-6 w-6 text-blue-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          Assets Recovered
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {statsInView && (
                            <CountUp
                              end={42}
                              prefix="$"
                              suffix="M+"
                              duration={2}
                            />
                          )}{" "}
                          in recovered cryptocurrency assets for our clients
                        </p>
                      </div>
                      <CheckCircle2 className="h-4 w-4 text-blue-500 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>

                  {/* Support Card */}
                  <div
                    className="p-5 rounded-xl bg-gradient-to-r from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 group/card hover:scale-[1.02] transition-all duration-300"
                    data-aos="zoom-in"
                    data-aos-delay="300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center border border-emerald-500/20 group-hover/card:scale-110 transition-transform">
                        <MessageSquare className="h-6 w-6 text-emerald-500" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-gray-900 dark:text-white mb-2">
                          24/7 Expert Support
                        </h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          Dedicated recovery specialists available around the
                          clock to assist with urgent cases and provide updates
                        </p>
                      </div>
                      <Zap className="h-4 w-4 text-emerald-500 opacity-0 group-hover/card:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-gray-50/50 to-transparent dark:from-gray-900/30">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/5 via-transparent to-primary/5 rounded-3xl blur-xl opacity-50"></div>
            <div className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl rounded-2xl border border-gray-200/30 dark:border-gray-700/30 p-8 lg:p-12">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                {[
                  {
                    value: 2022,
                    label: "Year Established",
                    color: "text-primary",
                    delay: 100,
                  },
                  {
                    value: 1500,
                    suffix: "+",
                    label: "Cases Handled",
                    color: "text-blue-500",
                    delay: 200,
                  },
                  {
                    value: 42,
                    suffix: "+",
                    label: "Expert Team",
                    color: "text-emerald-500",
                    delay: 300,
                  },
                  {
                    value: 24,
                    suffix: "/7",
                    label: "Support Availability",
                    color: "text-purple-500",
                    delay: 400,
                  },
                ].map((stat, idx) => (
                  <div
                    key={idx}
                    className="text-center group/stat"
                    data-aos="zoom-in"
                    data-aos-delay={stat.delay}
                  >
                    <div
                      className={`text-4xl lg:text-5xl font-bold ${stat.color} mb-2 group-hover/stat:scale-105 transition-transform`}
                    >
                      {statsInView && (
                        <CountUp
                          end={stat.value}
                          suffix={stat.suffix}
                          duration={2.5}
                        />
                      )}
                    </div>
                    <div className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-20">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent rounded-3xl blur-xl opacity-70"></div>
            <div
              className="relative bg-gradient-to-r from-primary to-primary/80 rounded-2xl p-8 lg:p-12 text-center backdrop-blur-sm border border-primary/30"
              data-aos="fade-up"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
                Ready to Recover Your Assets?
              </h2>
              <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
                Contact our expert recovery team for a free assessment of your
                case. We're here to help you reclaim what's rightfully yours.
              </p>
              <div
                className="flex flex-col sm:flex-row gap-4 justify-center"
                data-aos="zoom-in"
                data-aos-delay="200"
              >
                <Link
                  to="/register"
                  className="bg-white text-primary hover:bg-gray-100 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105 inline-flex items-center justify-center group/cta"
                >
                  <span>Start Recovery Process</span>
                  <ArrowRight className="h-5 w-5 ml-2 transition-transform group-hover/cta:translate-x-1" />
                </Link>
                <a
                  href="mailto:support@pelivorrecovery.org"
                  className="bg-transparent border-2 border-white text-white hover:bg-white/10 px-6 lg:px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:scale-105"
                >
                  <Sparkles className="h-5 w-5 mr-2 inline-block" />
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-12">
        <div className="container mx-auto px-4 lg:px-5">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src={logo} alt="Pelivor Recovery" className="h-8 w-auto" />
                <span className="text-xl font-bold text-white">
                  Pelivor Recovery
                </span>
              </div>
              <p className="mb-4 text-sm">
                Professional cryptocurrency recovery services with expertise in
                blockchain forensic analysis and asset recovery.
              </p>
              <div className="flex space-x-3">
                <Globe className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Users className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
                <Lock className="h-5 w-5 hover:text-white cursor-pointer transition-colors" />
              </div>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Services</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/recovery"
                    className="hover:text-white transition-colors"
                  >
                    Asset Recovery
                  </Link>
                </li>
                <li>
                  <Link
                    to="/forensics"
                    className="hover:text-white transition-colors"
                  >
                    Forensic Analysis
                  </Link>
                </li>
                <li>
                  <Link
                    to="/security"
                    className="hover:text-white transition-colors"
                  >
                    Security Consultation
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Company</h4>
              <ul className="space-y-2">
                <li>
                  <Link
                    to="/about"
                    className="hover:text-white transition-colors"
                  >
                    About Us
                  </Link>
                </li>
                <li>
                  <Link
                    to="/terms"
                    className="hover:text-white transition-colors"
                  >
                    Terms
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="hover:text-white transition-colors"
                  >
                    Privacy
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-white font-bold mb-4">Contact</h4>
              <p className="mb-2 text-sm">support@pelivorrecovery.org</p>
              <p className="text-sm">24/7 Recovery Support</p>
              <p className="text-sm mt-2">pelivorrecovery.org</p>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm">
            <p>
              © {new Date().getFullYear()} Pelivor Recovery. All rights
              reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default About;
